import { type ChangeEvent, type KeyboardEvent, useId, useRef, useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { MESSAGE_VARIABLES } from "@/lib/appointments";
import { cn } from "@/lib/utils";

type Variable = (typeof MESSAGE_VARIABLES)[number];

/**
 * A textarea for message templates: typing "#" offers the variables (filtered as you keep typing,
 * e.g. "#sto"); Enter, Tab or a click inserts `{{store_name}}` in place of what was typed.
 */
export function TemplateTextarea({
  id,
  value,
  onChange,
  rows = 4,
  invalid,
  describedBy,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  invalid?: boolean;
  describedBy?: string;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const listId = useId();
  const [query, setQuery] = useState<{ start: number; text: string } | null>(null);
  const [active, setActive] = useState(0);
  const options: readonly Variable[] = query
    ? MESSAGE_VARIABLES.filter((v) => {
        const q = query.text.toLowerCase().replace(/_/g, " ");
        return v.key.replace(/_/g, " ").includes(q) || v.label.toLowerCase().includes(q);
      })
    : [];
  const open = query !== null && options.length > 0;

  /** "#…" right before the caret, if any. */
  const findQuery = (text: string, caret: number) => {
    const match = /#([a-z_ ]{0,20})$/i.exec(text.slice(0, caret));
    if (!match || /\s\s/.test(match[1] ?? "")) return null;
    return { start: caret - match[0].length, text: match[1] ?? "" };
  };

  const change = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
    setQuery(findQuery(e.target.value, e.target.selectionStart ?? e.target.value.length));
    setActive(0);
  };

  const insert = (variable: Variable) => {
    const el = ref.current;
    if (!el || !query) return;
    const caret = el.selectionStart ?? value.length;
    const token = `{{${variable.key}}}`;
    const next = value.slice(0, query.start) + token + value.slice(caret);
    onChange(next);
    setQuery(null);
    const at = query.start + token.length;
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(at, at);
    });
  };

  const keyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!open) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => (a + (e.key === "ArrowDown" ? 1 : -1) + options.length) % options.length);
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      const option = options[active];
      if (option) insert(option);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setQuery(null);
    }
  };

  return (
    <div className="relative">
      <Textarea
        ref={ref}
        id={id}
        value={value}
        rows={rows}
        onChange={change}
        onKeyDown={keyDown}
        onBlur={() => setTimeout(() => setQuery(null), 150)}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open ? `${listId}-${options[active]?.key}` : undefined}
        className="font-mono text-[13px]"
      />
      {open ? (
        <div
          id={listId}
          role="listbox"
          aria-label="Variables"
          className="absolute top-full left-0 z-50 mt-1 max-h-56 w-64 overflow-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
        >
          {options.map((v, i) => (
            <div
              key={v.key}
              tabIndex={-1}
              id={`${listId}-${v.key}`}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => {
                e.preventDefault();
                insert(v);
              }}
              onMouseEnter={() => setActive(i)}
              className={cn(
                "flex cursor-pointer items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-sm",
                i === active && "bg-accent text-accent-foreground",
              )}
            >
              <span>{v.label}</span>
              <span className="font-mono text-muted-foreground text-xs">{`{{${v.key}}}`}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
